import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Col,
  Row,
  Table,
  Tag,
  DatePicker,
  Card,
  Spin,
  Select,
  Typography,
  Input,
  Tooltip,
} from 'antd';
import type { TableColumnType } from 'antd';
import dayjs from 'dayjs';
import BalanceRecordService, {
  BalanceRecordRes,
} from '../../service/account-balance-service/Balance-record.service';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;

// Enum định nghĩa các loại giao dịch cụ thể
enum TransactionEffect {
  MONEY_IN = 'money_in', // Tiền vào công ty
  MONEY_OUT = 'money_out', // Tiền ra khỏi công ty
  NEUTRAL = 'neutral', // Không ảnh hưởng
}

// Interface mở rộng để gắn hiển thị
interface TransactionTypeDisplay {
  effect: TransactionEffect;
  label: string;
  color: string;
  description: string;
}

const BalanceRecords = forwardRef<{ fetchBalanceRecord: () => void }, {}>(
  (_props, ref) => {
    const [balanceRecord, setBalanceRecord] = useState<BalanceRecordRes[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
    const [selectedTransactionType, setSelectedTransactionType] =
      useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [amountSearch, setAmountSearch] = useState<string>('');

    const fetchBalanceRecord = async () => {
      setLoading(true);
      try {
        const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;
        const response = await BalanceRecordService.getBalanceRecordsPaged(
          currentPage - 1, // backend 0-indexed
          pageSize,
          dateStr,
          selectedTransactionType,
          amountSearch ? amountSearch.replace(/\D/g, '') : ''
        );
        setBalanceRecord(response.content);
        setTotalRecords(response.totalElements);
      } catch (error) {
        console.error('Error fetching balance record:', error);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      const delayDebounceFn = setTimeout(() => {
        fetchBalanceRecord();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }, [currentPage, pageSize, selectedDate, selectedTransactionType, amountSearch]);

    const formatDate = (dateTime: string) => {
      const date = dayjs(dateTime).format('DD-MM-YYYY');
      const time = dayjs(dateTime).format('HH:mm:ss');
      return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
          <span>
            <span className="text-indigo-600">Ngày</span>{' '}
            <strong>{date}</strong>
          </span>
          <span>
            <span className="text-indigo-600">lúc</span> <strong>{time}</strong>
          </span>
        </div>
      );
    };

    // Hàm phân tích nội dung ghi chú để xác định loại giao dịch thực tế
    const analyzeTransaction = (
      record: BalanceRecordRes
    ): TransactionTypeDisplay => {
      const { amount, changeType, notes } = record;

      // Mặc định cho các loại không xác định
      let result: TransactionTypeDisplay = {
        effect: TransactionEffect.NEUTRAL,
        label: 'Không xác định',
        color: 'gray',
        description: 'Không thể xác định loại giao dịch',
      };

      // 1. Phân tích dựa trên ghi chú
      if (notes) {
        // Các giao dịch tiền về công ty (cộng tiền)
        if (
          notes.includes('tiền về từ giao dịch rút') ||
          notes.includes('Tiền về từ cập nhật giao dịch rút') ||
          notes.includes('Tiền về từ giao dịch rút chuyển trạng thái')
        ) {
          result = {
            effect: TransactionEffect.MONEY_IN,
            label: 'Tiền về',
            color: 'green',
            description: 'Tiền được cộng vào tài khoản từ giao dịch rút',
          };
        }
        // Tiền ra để nạp vào thẻ khách (deposit transactions)
        else if (
          notes.includes('Tiền ra để nạp vào thẻ khách') ||
          notes.includes('tiền ra để nạp vào thẻ khách')
        ) {
          result = {
            effect: TransactionEffect.MONEY_OUT,
            label: 'Tiền ra nạp thẻ',
            color: 'red',
            description: 'Tiền ra khỏi tài khoản để nạp vào thẻ khách hàng',
          };
        }
        // Tiền về từ giao dịch tiền mặt
        else if (notes.includes('Tiền về từ giao dịch tiền mặt')) {
          result = {
            effect: TransactionEffect.MONEY_IN,
            label: 'Tiền mặt',
            color: 'green',
            description: 'Tiền về từ giao dịch tiền mặt',
          };
        }
        // Hoàn trả do cập nhật/xóa giao dịch nạp
        else if (
          notes.includes('Hoàn trả do cập nhật giao dịch nạp') ||
          notes.includes('Hoàn trả do xóa giao dịch nạp')
        ) {
          result = {
            effect: TransactionEffect.MONEY_IN,
            label: 'Hoàn tiền nạp',
            color: 'green',
            description: 'Tiền được hoàn lại do cập nhật/xóa giao dịch nạp',
          };
        }
        // Hoàn trả do cập nhật/xóa giao dịch rút
        else if (
          notes.includes('Hoàn trả do cập nhật giao dịch rút') ||
          notes.includes('Hoàn trả do xóa giao dịch rút')
        ) {
          result = {
            effect: TransactionEffect.MONEY_OUT,
            label: 'Hoàn tiền rút',
            color: 'red',
            description: 'Tiền bị trừ do cập nhật/xóa giao dịch rút',
          };
        }
        // Xóa giao dịch withdrawal trước đó
        else if (
          notes.includes('Xóa mềm giao dịch') &&
          notes.includes('withdrawal')
        ) {
          result = {
            effect: TransactionEffect.MONEY_OUT,
            label: 'Hoàn tiền rút',
            color: 'red',
            description: 'Tiền bị trừ do xóa giao dịch rút trước đó',
          };
        }
        // Xóa giao dịch deposit trước đó
        else if (
          notes.includes('Xóa mềm giao dịch') &&
          notes.includes('deposit')
        ) {
          result = {
            effect: TransactionEffect.MONEY_IN,
            label: 'Hoàn tiền nạp',
            color: 'green',
            description: 'Tiền được hoàn lại do xóa giao dịch nạp trước đó',
          };
        }
        // Hoàn tiền do thay đổi trạng thái giao dịch
        else if (
          notes.includes('Hoàn tiền do giao dịch rút chuyển trạng thái')
        ) {
          result = {
            effect: TransactionEffect.MONEY_OUT,
            label: 'Hoàn tiền rút',
            color: 'red',
            description: 'Tiền bị trừ do thay đổi trạng thái giao dịch rút',
          };
        }
        // Chuyển trạng thái thanh toán
        else if (notes.includes('Chuyển trạng thái thanh toán')) {
          if (amount > 0) {
            result = {
              effect: TransactionEffect.MONEY_IN,
              label: 'Trạng thái + ',
              color: 'green',
              description: 'Tiền được cộng do thay đổi trạng thái thanh toán',
            };
          } else {
            result = {
              effect: TransactionEffect.MONEY_OUT,
              label: 'Trạng thái - ',
              color: 'red',
              description: 'Tiền bị trừ do thay đổi trạng thái thanh toán',
            };
          }
        }
      }

      // 2. Nếu không phân tích được từ ghi chú, dùng loại giao dịch và số tiền thực tế
      if (result.label === 'Không xác định') {
        // Sử dụng số tiền thực tế để xác định hướng giao dịch
        if (amount > 0) {
          result = {
            effect: TransactionEffect.MONEY_IN,
            label: 'Tiền vào',
            color: 'green',
            description: 'Tiền được cộng vào tài khoản',
          };
        } else if (amount < 0) {
          result = {
            effect: TransactionEffect.MONEY_OUT,
            label: 'Tiền ra',
            color: 'red',
            description: 'Tiền bị trừ khỏi tài khoản',
          };
        } else {
          result = {
            effect: TransactionEffect.NEUTRAL,
            label: 'Không thay đổi',
            color: 'gray',
            description: 'Không có thay đổi số dư',
          };
        }
      }

      // 3. Đảm bảo màu sắc phù hợp với số tiền
      if (amount < 0 && result.effect === TransactionEffect.MONEY_IN) {
        console.warn(
          'Mâu thuẫn: Loại giao dịch là tiền vào nhưng số tiền âm',
          record
        );
      } else if (amount > 0 && result.effect === TransactionEffect.MONEY_OUT) {
        console.warn(
          'Mâu thuẫn: Loại giao dịch là tiền ra nhưng số tiền dương',
          record
        );
      }

      return result;
    };

    // Hiển thị loại giao dịch với tooltip giải thích
    const renderTransactionType = (
      changeType: string,
      record: BalanceRecordRes
    ) => {
      const transactionInfo = analyzeTransaction(record);

      return (
        <Tooltip title={transactionInfo.description}>
          <Tag color={transactionInfo.color}>
            {transactionInfo.label}
            <InfoCircleOutlined style={{ marginLeft: 4 }} />
          </Tag>
        </Tooltip>
      );
    };

    const columns: TableColumnType<BalanceRecordRes>[] = [
      {
        title: 'Ngày',
        dataIndex: 'dateTime',
        key: 'dateTime',
        width: 180,
        render: (dateTime: string) => {
          const date = dayjs(dateTime).format('DD-MM-YYYY');
          const time = dayjs(dateTime).format('HH:mm');
          return (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{date}</span>
              <span className="text-xs text-gray-500">{time}</span>
            </div>
          );
        },
        responsive: ['sm'],
      },
      {
        title: 'Thời gian',
        dataIndex: 'dateTime',
        key: 'dateTimeMobile',
        render: (dateTime: string) =>
          dayjs(dateTime).format('DD-MM-YYYY HH:mm'),
        responsive: ['xs'],
      },
      {
        title: 'Số tiền',
        dataIndex: 'amount',
        key: 'amount',
        width: 140,
        render: (amount: number, record: BalanceRecordRes) => {
          const transactionInfo = analyzeTransaction(record);
          const formattedAmount =
            Math.floor(Math.abs(amount)).toLocaleString('vi-VN') + ' đ';

          // Sử dụng dấu thực tế của số tiền thay vì dựa vào effect
          const displayAmount = (
            <Tooltip title={transactionInfo.description}>
              <span
                style={{ color: transactionInfo.color, fontWeight: 'bold' }}
              >
                {amount >= 0 ? '+' : '-'}
                {formattedAmount}
              </span>
            </Tooltip>
          );

          return displayAmount;
        },
        filterDropdown: () => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm theo số tiền"
              value={amountSearch}
              onChange={(e) => handleAmountSearch(e.target.value)}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
      },
      {
        title: 'Số dư trước',
        dataIndex: 'balanceBefore',
        key: 'balanceBefore',
        width: 140,
        render: (balance: number) =>
          balance !== null && balance !== undefined ? Math.floor(balance).toLocaleString('vi-VN') : '-',
      },
      {
        title: 'Số dư sau',
        dataIndex: 'balanceAfter',
        key: 'balanceAfter',
        width: 140,
        render: (balance: number) =>
          balance !== null && balance !== undefined ? Math.floor(balance).toLocaleString('vi-VN') : '-',
      },
      {
        title: 'Loại GD',
        dataIndex: 'changeType',
        key: 'changeType',
        width: 120,
        render: renderTransactionType,
      },
      {
        title: 'Ghi chú',
        dataIndex: 'notes',
        key: 'notes',
        responsive: ['sm'],
        width: 450,
        render: (notes: string) => {
          if (!notes) return '';

          // 1. Loại bỏ prefix nhân viên: **STT[id]-[tên]** - 
          let formattedNotes = notes.replace(/^\*\*STT\d+-.*?\*\* - /, '');

          // 2. Loại bỏ Transaction ID: - Transaction ID: [số]
          formattedNotes = formattedNotes.replace(/ - Transaction ID: \d+/, '');

          // 3. Loại bỏ ID nhân viên trong ngoặc nếu có (thường ở giao dịch chuyển tiền): (ID: [số])
          formattedNotes = formattedNotes.replace(/ \(ID: \d+\)/, '');

          return (
            <div
              style={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.4',
              }}
            >
              {formattedNotes}
            </div>
          );
        },
      },
    ];

    // Hàm xử lý tìm kiếm số tiền
    const handleAmountSearch = (value: string) => {
      setCurrentPage(1);
      setAmountSearch(value);
    };

    useImperativeHandle(ref, () => ({
      fetchBalanceRecord,
    }));

    const transactionOptions = [
      { value: '', label: 'Tất cả' },
      { value: 'deposit', label: 'Chuyển tiền ra', color: 'red' },
      { value: 'withdrawal', label: 'Tiền về', color: 'green' },
      {
        value: 'transaction_deletion',
        label: 'Xóa giao dịch',
        color: 'orange',
      },
    ];

    return (
      <Card className="mt-4">
        <div className="mb-4">
          <Title level={4}>Lịch sử giao dịch</Title>
          <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="mb-2 font-medium">Hướng dẫn đọc bảng:</p>
            <div className="flex flex-wrap gap-2 mb-2">
              <Tag color="green">+XX.XXX đ</Tag>
              <span>Tiền vào tài khoản công ty</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Tag color="red">-XX.XXX đ</Tag>
              <span>Tiền ra khỏi tài khoản công ty</span>
            </div>
          </div>
        </div>
        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} sm={12} md={6} lg={6}>
            <DatePicker
              format="DD-MM-YYYY"
              placeholder="Chọn ngày"
              onChange={(date) => {
                setCurrentPage(1);
                setSelectedDate(date);
              }}
              value={selectedDate}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <Select
              placeholder="Chọn loại giao dịch"
              value={selectedTransactionType}
              onChange={(value) => {
                setCurrentPage(1);
                setSelectedTransactionType(value);
              }}
              style={{ width: '100%' }}
              options={transactionOptions.map((option) => ({
                value: option.value,
                label: (
                  <span>
                    {option.color && option.value ? (
                      <Tag color={option.color}>{option.label}</Tag>
                    ) : (
                      option.label
                    )}
                  </span>
                ),
              }))}
            />
          </Col>
        </Row>
        <Spin spinning={loading}>
          <Table
            dataSource={balanceRecord}
            columns={columns}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRecords,
              responsive: true,
              showSizeChanger: false,
              showTotal: (total) => `Tổng số ${total} giao dịch`,
              onChange: (page, size) => {
                setCurrentPage(page);
              },
            }}
          />
        </Spin>
      </Card>
    );
  }
);

export default BalanceRecords;
